import styled from 'styled-components'

import {Inline, Stack, Text, tokens} from '@pleo-io/telescope'
import {Bars, Calculator, ShieldLock} from '@pleo-io/telescope-icons'

import type {PageOrigin} from '@product-web/paywall/types'
import {pxBreakpoints} from '@product-web/styles/theme'
import {useContainerQuery} from '@product-web/telescope-lab/container-queries/container'

import type {LandingPageKeyCardProps} from '../components/landing-page-key-card'
import {useLocalisedAnimation} from '../use-localised-animation'

export type MediaAndKeyCardsProps = {
    pageOrigin: PageOrigin
    cards: LandingPageKeyCardProps[]
}

const SmallerIcons = [
    <ShieldLock size={24} key="shield-lock" />,
    <Calculator size={24} key="calculator" />,
    <Bars size={24} key="bars" />,
]

export const MediaAndKeyCards = ({pageOrigin, cards}: MediaAndKeyCardsProps) => {
    const {animationSrc, animationAlt} = useLocalisedAnimation({
        type: pageOrigin,
    })

    const isDesktop = useContainerQuery({
        name: 'main',
        minWidth: pxBreakpoints.tabletMedUp,
    })

    return (
        <StyledInline $isDesktop={isDesktop} data-testid="media-and-key-cards-section">
            <img src={animationSrc} alt={animationAlt} width={isDesktop ? '50%' : '100%'} />

            <div css={{alignSelf: 'stretch', display: 'flex'}}>
                <StyledStack
                    $isDesktop={isDesktop}
                    space={28}
                    stretch
                    css={{alignContent: 'stretch'}}
                >
                    {cards.map((keyCardProps, index) => (
                        <KeyCard
                            {...keyCardProps}
                            imageIcon={SmallerIcons[index]}
                            key={keyCardProps.getTitle()}
                        />
                    ))}
                </StyledStack>
            </div>
        </StyledInline>
    )
}

const StyledStack = styled(Stack)<{$isDesktop: boolean}>`
    padding: ${({$isDesktop}) => ($isDesktop ? 0 : `${tokens.spacing16} ${tokens.spacing28}`)};
`

const KeyCard = ({imageIcon, getTitle, getDescription}: LandingPageKeyCardProps) => {
    return (
        <Inline css={{gap: tokens.spacing24}} data-testid="new-landing-page-key-card">
            <div data-testid="key-card-small-icon">{imageIcon}</div>
            <Stack space={8}>
                <Text variant="large-accent" as="h3" weight="medium">
                    {getTitle()}
                </Text>
                <Text variant="medium-default" as="p" color="colorContentStaticQuiet">
                    {getDescription()}
                </Text>
            </Stack>
        </Inline>
    )
}

const StyledInline = styled(Inline)<{$isDesktop: boolean}>`
    gap: ${({$isDesktop}) => ($isDesktop ? tokens.spacing32 : tokens.spacing24)};
    flex-direction: ${({$isDesktop}) => ($isDesktop ? 'row' : 'column')};
    padding-top: ${tokens.spacing48};
    width: 100%;
`
